import { 
  Component, 
  OnInit,
  OnChanges,
  OnDestroy,
  Input,
  SimpleChanges,
  EventEmitter,
  Output,
  ViewChild,
  Inject,
  ChangeDetectorRef
} from '@angular/core';

import { 
  FormGroup,
  FormControl,
  Validators 
} from '@angular/forms';

import { isEqual, isNil } from 'lodash-es';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ViewerComponent } from '@mescius/activereportsjs-angular';
import { BaseComponent } from './components/base.component';

import { SharedModule } from './shared.module';
import { UtilsService } from './utils.service';
import { SettingsValuesService } from './settings.values.service';
import { SalesOrders_ShellService } from './SalesOrders.shell.service';
import { SalesOrders_OperationService } from './SalesOrders.operation.service';
import { SalesOrders_DatasourceService } from './SalesOrders.datasource.index';
import { SalesOrders_FlowService } from './SalesOrders.flow.index';
import { SalesOrders_ReportService } from './SalesOrders.report.index';
import { SalesOrders_LocalizationService } from './SalesOrders.localization.service';
import { SalesOrders_outbound_bill_of_lading_report_ComponentContextService } from './SalesOrders.outbound_bill_of_lading_report.component.context.service';
import { Language } from './localization.service';
import { JobStatus } from './common-interfaces'
import { ApplicationType, ComponentType } from './app-context.service';
import { CleanupLoggerService } from './cleanup.logging.service';
import { $frontendTypes} from './SalesOrders.frontend.types'
import { $frontendTypes as $types} from './SalesOrders.frontend.types' 

import { EModalSize, EToasterType, EToasterPosition } from 'wavelength-ui';

@Component({
  standalone: true,
  imports: [
    SharedModule,
  ],
  selector: 'SalesOrders-outbound_bill_of_lading_report',
  templateUrl: './SalesOrders.outbound_bill_of_lading_report.component.html'
})
export class SalesOrders_outbound_bill_of_lading_reportComponent extends BaseComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('reportViewer', { static: false }) reportViewer: ViewerComponent;

  report: any;
  reportParameters: any;

  inParams: { orderId: number, shipmentId?: number } = { orderId: null, shipmentId: null };
  //#region Inputs
  @Input('orderId') set $inParams_orderId(v: number) {
    this.inParams.orderId = v;
  }
  get $inParams_orderId(): number {
    return this.inParams.orderId;
  }
  @Input('shipmentId') set $inParams_shipmentId(v: number) {
    this.inParams.shipmentId = v;
  }
  get $inParams_shipmentId(): number {
    return this.inParams.shipmentId;
  }
  //#endregion Inputs

  //#region Outputs
  @Output()
  $finish = new EventEmitter();
  @Output()
  $refreshEvent = new EventEmitter();
  //#endregion Outputs

  //#region title
  // Make it async so that it won't cause expressionChangedAfterItHasBeenCheckedError
  // The title is often meant to be shown from the parent (shell breadcrumb for example)
  // and often it will cause an expressionChangedAfterItHasBeenCheckedError because 
  // the parent has already been checked and the child now change something on the parent 
  // in dev, CD is run twice
  $titleChange = new EventEmitter<string>(true);
  private $_title: string;
  get title(): string {
    return this.$_title;
  }
  set title(t: string) {
    this.$_title = t;
    this.$titleChange.emit(this.$_title);
  }
  //#endregion title
  //#region Variables
  //#endregion
  constructor(
    private $utils: UtilsService,
    private $settings: SettingsValuesService,
    private $shell: SalesOrders_ShellService,
    private $datasources: SalesOrders_DatasourceService,
    private $flows: SalesOrders_FlowService,
    private $reports: SalesOrders_ReportService,
    private $localization: SalesOrders_LocalizationService,
    private $operations: SalesOrders_OperationService,
    private $logger: CleanupLoggerService,
    private $context: SalesOrders_outbound_bill_of_lading_report_ComponentContextService,
    private changeDetector: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.$checkRequiredInParams();
    if (!this.$hasMissingRequiredInParams) {
      this.$init();
    } else {
      this.$initEmpty();
    }
  }
  
  private $isFirstNgOnChanges = true;
  ngOnChanges(changes: SimpleChanges): void {
    if (this.$isFirstNgOnChanges) {
      this.$isFirstNgOnChanges = false;
    } else {
      this.$checkRequiredInParams();
      if(!this.$hasMissingRequiredInParams) {
        this.$init();
      } else {
        this.$initEmpty();
      }
    }
  }

  private $unsubscribe$ = new Subject();
  ngOnDestroy(): void {
    this.$unsubscribe$.next(null);
    this.$unsubscribe$.complete();
  }
  $missingRequiredInParams = [];
  get $hasMissingRequiredInParams(): boolean {
    return !!this.$missingRequiredInParams.length;
  }
  
  $checkRequiredInParams() {
    this.$missingRequiredInParams = [];
      if(isNil(this.inParams.orderId)) {
        this.$missingRequiredInParams.push('orderId');
      }
  }

  initialized = false;

  async $init() {
    this.title = 'Bill of lading';
    await this.$dataLoad();
    this.initialized = true;
  }

  async $dataLoad() {
    const $report = this;
    const $utils = this.$utils;

    // We want to use reportViewer.open only in the onViewerInit, otherwise
    // the reportViewer might not be ready. In order to do that, the viewer has to be recreated.
    // We recreate it by using *ngIf="report"

    // set the report to null so that the reportViewer gets destroyed (initially this will change nothing)
    this.report = null;

    // actually destroy the viewer
    this.changeDetector.detectChanges();

    // set the report to the actual one which should create the viewer
    this.report = await this.$reports.SalesOrders.outbound_bill_of_lading_report.getReport(this.inParams);
    this.reportParameters = this.$reports.SalesOrders.outbound_bill_of_lading_report.getReportParameters(this.inParams);
  }

  onViewerInit() {
    this.reportViewer?.open(this.report, {
        ReportParams: this.reportParameters
      });
  }

  refresh(
    skipParent = false,
    skipChildren = false,
    childToSkip: string = null) {
    if (this.$hasMissingRequiredInParams) {
      return Promise.resolve(null);
    }
    // up
    if (skipParent === false) {
      this.$refreshEvent.emit();
    }

    // self
    const result = this.$dataLoad();

    // children
    
    return result;
  }

  close() {
    this.$finish.emit();
  }
}
